// app/javascript/controllers/uploads_controller.js
import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"
import Swal from 'sweetalert2'

export default class extends Controller {
  static targets = ["progressBar"]

  static values = {
    recordId: String,
    modelName: String,
    attributeName: String,
    acceptedTypes: String,
    multiple: Boolean,
    maxSize: Number,
    attachUrl: String
  }

  connect() {
    this.pendingUploads = 0
    this.uploadQueue = []
    this.processing = false

    // Vérifier s'il y a des fichiers existants pour mettre à jour l'UI
    const filesListElement = this.element.querySelector(`#${this.modelNameValue}_${this.attributeNameValue}_files`)
    if (filesListElement) {
      this.hasExistingFiles = filesListElement.querySelectorAll('li').length > 0
    }

    // Empêcher le comportement par défaut du navigateur pour le drag & drop
    this.preventDefaults = this.preventDefaults.bind(this)
    this.dropzone = this.element.querySelector('.upload-dropzone')

    if (this.dropzone) {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        this.dropzone.addEventListener(eventName, this.preventDefaults, false)
        document.body.addEventListener(eventName, this.preventDefaults, false)
      })
    }
  }

  disconnect() {
    // Nettoyer les event listeners
    if (this.dropzone) {
      ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
        this.dropzone.removeEventListener(eventName, this.preventDefaults, false)
        document.body.removeEventListener(eventName, this.preventDefaults, false)
      })
    }

    // Ne pas nettoyer si nous sommes en cours d'upload
    if (this.pendingUploads > 0 || this.uploadQueue.length > 0) {
      return
    }

    // Nettoyer uniquement si nous quittons la page complètement
    if (!Turbo.navigator.formSubmission) {
      fetch('/temporary_attachments/cleanup', {
        method: 'DELETE',
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })
      .catch(() => {})
    }
  }

  preventDefaults(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  handleDragEnter(event) {
    this.preventDefaults(event)
    this.dropzone.classList.add('dragover')
  }

  handleDragOver(event) {
    this.preventDefaults(event)
    this.dropzone.classList.add('dragover')
  }

  handleDragLeave(event) {
    this.preventDefaults(event)
    // Vérifier si on quitte vraiment la zone
    if (!this.dropzone.contains(event.relatedTarget)) {
      this.dropzone.classList.remove('dragover')
    }
  }

  async handleDrop(event) {
    this.preventDefaults(event)
    this.dropzone.classList.remove('dragover')

    const files = Array.from(event.dataTransfer.files)
    if (files.length > 0) {
      await this.processFiles(files)
    }
  }

  async previewFiles(event) {
    const files = Array.from(event.target.files || [])
    if (files.length === 0) return

    await this.processFiles(files)
  }

  async processFiles(files) {
    const maxSize = parseInt(this.maxSizeValue) || 10485760 // 10MB par défaut
    const maxFiles = this.multipleValue ? 10 : 1

    // Validation des fichiers
    const isValid = await this.validateFiles(files, maxSize, maxFiles)
    if (!isValid) {
      return
    }

    // Ajouter à la file d'attente et commencer le traitement
    this.uploadQueue.push(...files)
    this.processNextInQueue()
  }

  async validateFiles(files, maxSize, maxFiles) {
    // Vérifier si nous dépassons la limite de fichiers multiples
    if (!this.multipleValue && this.hasExistingFiles) {
      return await this.showFileReplacementWarning()
    } else if (this.multipleValue && files.length + this.getExistingFilesCount() > maxFiles) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: 'var(--upload-accent-color, #E97451)',
        title: 'Trop de fichiers',
        text: `Vous ne pouvez pas télécharger plus de ${maxFiles} fichier${maxFiles > 1 ? 's' : ''}`
      })
      return false
    }

    // Vérifier la taille des fichiers
    const oversizedFiles = files.filter(file => file.size > maxSize)
    if (oversizedFiles.length > 0) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: 'var(--upload-accent-color, #E97451)',
        title: 'Fichier(s) trop volumineux',
        text: `La taille maximale autorisée est de ${(maxSize/1024/1024).toFixed(1)}MB`
      })
      return false
    }

    // Vérifier les types de fichiers
    const acceptedTypes = this.acceptedTypesValue.split(',').map(type => type.trim())
    const invalidFiles = files.filter(file => {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase()
      return !acceptedTypes.includes(fileExtension)
    })

    if (invalidFiles.length > 0) {
      Swal.fire({
        icon: 'error',
        confirmButtonColor: 'var(--upload-accent-color, #E97451)',
        title: 'Type de fichier non autorisé',
        text: `Types acceptés : ${this.acceptedTypesValue}`
      })
      return false
    }

    return true
  }

  showFileReplacementWarning() {
    return Swal.fire({
      icon: 'info',
      confirmButtonColor: 'var(--upload-accent-color, #E97451)',
      title: 'Remplacement de fichier',
      text: 'Le fichier existant sera remplacé par le nouveau fichier.',
      showCancelButton: true,
      cancelButtonText: 'Annuler',
      confirmButtonText: 'Continuer'
    }).then((result) => {
      return result.isConfirmed
    })
  }

  getExistingFilesCount() {
    const filesElement = document.getElementById(`${this.modelNameValue}_${this.attributeNameValue}_files`)
    if (!filesElement) return 0
    return filesElement.querySelectorAll('li').length
  }

  processNextInQueue() {
    if (this.processing || this.uploadQueue.length === 0) return

    this.processing = true
    const nextFile = this.uploadQueue.shift()
    this.uploadFile(nextFile)
  }

  uploadFile(file) {
    this.pendingUploads++
    this.dropzone.classList.add('upload-active')

    // Mise à jour de la barre de progression
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '0%'
    }

    // Créer l'upload direct
    const upload = new DirectUpload(
      file,
      '/rails/active_storage/direct_uploads',
      this
    )

    // Commencer l'upload
    upload.create((error, blob) => {
      if (error) {
        Swal.fire({
          icon: 'error',
          title: 'Erreur d\'upload',
          text: 'Une erreur est survenue pendant le téléchargement du fichier.',
          confirmButtonColor: 'var(--upload-accent-color, #E97451)'
        })
        this.completeUpload()
        return
      }

      // Attacher le fichier au record
      this.attachFile(blob)
    })
  }

  attachFile(blob) {
    fetch(this.attachUrlValue, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        signed_blob_id: blob.signed_id,
        attribute_name: this.attributeNameValue,
        accepted_types: this.acceptedTypesValue,
        model_name: this.modelNameValue,
        multiple: this.multipleValue
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`)
      }
      return response.text()
    })
    .then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
      }

      // Marquer que nous avons des fichiers existants maintenant
      this.hasExistingFiles = true

      this.completeUpload()
    })
    .catch(error => {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue lors de l\'enregistrement du fichier.',
        confirmButtonColor: 'var(--upload-accent-color, #E97451)'
      })
      this.completeUpload()
    })
  }

  completeUpload() {
    this.pendingUploads--
    this.processing = false
    this.dropzone.classList.remove('upload-active')

    // Réinitialiser l'état de l'upload
    if (this.pendingUploads === 0) {
      const fileInput = this.element.querySelector('input[type="file"]')
      if (fileInput) fileInput.value = ''

      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = '0%'
      }
    }

    // Traiter le prochain fichier dans la file d'attente
    this.processNextInQueue()
  }

  confirmTempDelete(event) {
    event.preventDefault()
    const button = event.currentTarget
    const title = button.dataset.uploadsConfirmTitleValue || "Supprimer ce fichier ?"
    const text = button.dataset.uploadsConfirmTextValue || "Cette action est irréversible."
    const confirmButton = button.dataset.uploadsConfirmButtonValue || "Oui, supprimer"
    const cancelButton = button.dataset.uploadsCancelButtonValue || "Annuler"

    Swal.fire({
      title: title,
      text: text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626', // red-600
      cancelButtonColor: 'var(--upload-primary-color, #6b7280)', // gray-500
      confirmButtonText: confirmButton,
      cancelButtonText: cancelButton,
      reverseButtons: true,
      customClass: {
        popup: 'upload-delete-modal',
        title: 'upload-delete-title',
        content: 'upload-delete-content'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // Procéder à la suppression du fichier temporaire
        this.executeRemoveTemporaryFile(button)
      }
    })
  }

  executeRemoveTemporaryFile(button) {
    const modelName = button.dataset.uploadsModelName
    const attributeName = button.dataset.uploadsAttributeName
    const blobSignedId = button.dataset.uploadsBlobSignedId
    const multiple = button.dataset.uploadsMultiple === 'true'

    // Ajouter un indicateur de chargement
    const icon = button.querySelector('i')
    const originalClass = icon.className
    icon.className = 'fa-solid fa-spinner fa-spin'

    fetch('/temporary_attachments/destroy', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
      },
      body: JSON.stringify({
        model_name: modelName,
        attribute_name: attributeName,
        blob_signed_id: blobSignedId,
        multiple: multiple
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`)
      }
      return response.text()
    })
    .then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
      }

      // Mettre à jour le compteur de fichiers existants
      this.hasExistingFiles = this.getExistingFilesCount() > 0

      // Réinitialiser l'input file pour permettre de re-sélectionner le même fichier
      const fileInput = this.element.querySelector('input[type="file"]')
      if (fileInput) fileInput.value = ''

      // Afficher un message de succès
      Swal.fire({
        title: 'Retiré !',
        text: 'Le fichier a été retiré de la liste.',
        icon: 'success',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        customClass: {
          popup: 'upload-success-toast'
        }
      })
    })
    .catch(error => {
      // Restaurer l'icône originale
      icon.className = originalClass

      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue lors de la suppression du fichier.',
        confirmButtonColor: 'var(--upload-accent-color, #E97451)'
      })
    })
  }

  // Garder l'ancienne méthode pour compatibilité
  removeTemporaryFile(event) {
    // Rediriger vers la nouvelle méthode avec confirmation
    this.confirmTempDelete(event)
  }

  confirmDelete(event) {
    event.preventDefault()
    const link = event.currentTarget
    const title = link.dataset.uploadsConfirmTitleValue || "Supprimer ce fichier ?"
    const text = link.dataset.uploadsConfirmTextValue || "Cette action est irréversible."
    const confirmButton = link.dataset.uploadsConfirmButtonValue || "Oui, supprimer"
    const cancelButton = link.dataset.uploadsCancelButtonValue || "Annuler"

    Swal.fire({
      title: title,
      text: text,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626', // red-600
      cancelButtonColor: 'var(--upload-primary-color, #6b7280)', // gray-500
      confirmButtonText: confirmButton,
      cancelButtonText: cancelButton,
      reverseButtons: true,
      customClass: {
        popup: 'upload-delete-modal',
        title: 'upload-delete-title',
        content: 'upload-delete-content'
      }
    }).then((result) => {
      if (result.isConfirmed) {
        // Procéder à la suppression - utiliser Turbo directement
        this.executeDeleteWithTurbo(link)
      }
    })
  }

  executeDeleteWithTurbo(link) {
    // Ajouter un indicateur de chargement
    const icon = link.querySelector('i')
    const originalClass = icon.className
    icon.className = 'fa-solid fa-spinner fa-spin'

    // Utiliser Turbo pour effectuer la requête DELETE
    link.click = null // Empêcher le double-clic

    fetch(link.href, {
      method: 'DELETE',
      headers: {
        'Accept': 'text/vnd.turbo-stream.html',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content,
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`)
      }
      return response.text()
    })
    .then(html => {
      if (html) {
        Turbo.renderStreamMessage(html)
      }

      // Mettre à jour le compteur de fichiers existants
      this.hasExistingFiles = this.getExistingFilesCount() > 0

      // Afficher un message de succès
      Swal.fire({
        title: 'Supprimé !',
        text: 'Le fichier a été supprimé avec succès.',
        icon: 'success',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
        customClass: {
          popup: 'upload-success-toast'
        }
      })
    })
    .catch(error => {
      // Restaurer l'icône originale
      icon.className = originalClass

      Swal.fire({
        icon: 'error',
        title: 'Erreur de suppression',
        text: 'Une erreur est survenue lors de la suppression du fichier.',
        confirmButtonColor: 'var(--upload-accent-color, #E97451)'
      })
    })
  }

  directUploadWillStoreFileWithXHR(xhr) {
    xhr.upload.addEventListener("progress", event => {
      const progress = event.loaded / event.total * 100
      if (this.hasProgressBarTarget) {
        this.progressBarTarget.style.width = `${progress}%`
      }
    })
  }
}
